/**
 * Custom React hook for authentication state management.
 */

import { useState, useEffect } from 'react';
import { login as apiLogin } from '@/services/api';
import type { LoginRequest } from '@/types';

interface AuthState {
    isAuthenticated: boolean;
    isLoading: boolean;
    token: string | null;
}

export const useAuth = () => {
    const [authState, setAuthState] = useState<AuthState>(() => {
        // Initialize synchronously from localStorage to avoid race conditions
        const token = localStorage.getItem('access_token');
        return {
            isAuthenticated: !!token,
            isLoading: false,
            token,
        };
    });

    useEffect(() => {
        // Check for existing token on mount AND whenever localStorage changes
        const checkAuth = () => {
            const token = localStorage.getItem('access_token');
            setAuthState({
                isAuthenticated: !!token,
                isLoading: false,
                token,
            });
        };

        checkAuth();

        // Listen for storage changes (from other tabs/windows)
        window.addEventListener('storage', checkAuth);

        return () => {
            window.removeEventListener('storage', checkAuth);
        };
    }, []);

    const login = async (credentials: LoginRequest): Promise<void> => {
        try {
            const response = await apiLogin(credentials);
            const token = response.access_token;
            
            console.log('✅ Login successful, setting token:', token.substring(0, 20) + '...');
            
            // Set token in localStorage first
            localStorage.setItem('access_token', token);
            
            // Verify it was set
            const storedToken = localStorage.getItem('access_token');
            console.log('✅ Token stored in localStorage:', storedToken?.substring(0, 20) + '...');
            
            // Then update state
            setAuthState({
                isAuthenticated: true,
                isLoading: false,
                token,
            });
        } catch (error) {
            console.error('❌ Login failed:', error);
            // Clear any partial auth state on error
            setAuthState({
                isAuthenticated: false,
                isLoading: false,
                token: null,
            });
            throw error;
        }
    };

    const logout = (): void => {
        localStorage.removeItem('access_token');
        setAuthState({
            isAuthenticated: false,
            isLoading: false,
            token: null,
        });
    };

    const checkAuth = (): void => {
        const token = localStorage.getItem('access_token');
        setAuthState({
            isAuthenticated: !!token,
            isLoading: false,
            token,
        });
    };

    return {
        ...authState,
        login,
        logout,
        checkAuth, // Export for manual refresh
    };
};
